<script setup lang="ts">
import { range } from 'lodash';
import { ArrowLeft, ArrowRight, Info } from 'lucide-vue-next';
import { computed } from 'vue';

import useSearchParamsState from '~/hooks/useSearchParamsState';
import useUsers, { UseUsersParams } from '~/store/users/useUsers';
import date from '~/utils/date';
import getErrorMessage from '~/utils/getErrorMessage';
import paginationUtils from '~/utils/pagination';

import DashboardLayout from '../layouts/DashboardLayout.vue';

const DEFAULT_PAGE_SIZE = 10;

const filters = useSearchParamsState<UseUsersParams>({
  email: '',
  only_gc: false,
  page: 1,
  page_size: DEFAULT_PAGE_SIZE,
  partial_name: '',
  roles: '',
});

const {
  data: users,
  error: errorUsers,
  isError: isErrorUsers,
  isFetching: isFetchingUsers,
  isLoading: isLoadingUsers,
  isSuccess: isSuccessUsers,
} = useUsers(filters);

// computeds
const pageSize = computed(() => Number(filters.value.page_size) || DEFAULT_PAGE_SIZE);
const totalItems = computed(() => users.value?.count || 10);
const totalPages = computed(() => Math.ceil(totalItems.value / pageSize.value));
const currentPage = computed(() => Number(filters.value.page) || 1);

const formPagination = computed(() => paginationUtils.generatePaginationBlocks({
  currentPage: currentPage.value,
  totalPages: totalPages.value,
}));

function _handleSetPage(page: number) {
  if (!users.value?.count) {
    return;
  }

  if (page < 1) {
    filters.value.page = 1;
    return;
  } if (page > users.value?.count) {
    return;
  }
  filters.value.page = Number(page);
}

function handleNextPage() {
  if (!filters.value.page) return;
  _handleSetPage(Number(filters.value.page) + 1);
}

function handlePreviousPage() {
  if (!filters.value.page) return;
  _handleSetPage(Number(filters.value.page) - 1);
}
</script>

<template>
  <DashboardLayout title="Usuários" description="List of users" :loading="isFetchingUsers || isLoadingUsers">
    <div class="row row-cards">
      <div class="col-12">
        <!-- error state -->
        <div v-if="isErrorUsers">
          <div class="alert alert-danger">
            <div class="alert-title">Erro ao carregar usuários</div>
            <p>Ocorreu um erro ao carregar os usuários. Tente novamente mais tarde.</p>
            <p>{{ getErrorMessage(errorUsers) }}</p>
          </div>
        </div>
        <!-- loading/success state -->
        <div v-else-if="isSuccessUsers" class="card">
          <div class="card-body border-bottom py-3">
            ... <!-- Placeholder for filters -->
          </div>
          <div class="table-responsive">
            <table class="table table-selectable card-table table-vcenter text-nowrap datatable">
              <thead>
                <tr>
                  <th class="w-1">
                    <input class="form-check-input m-0 align-middle" type="checkbox" aria-label="Select all invoices">
                  </th>
                  <th class="w-1">
                    ID
                  </th>
                  <th>Nome</th>
                  <th>Parceiro</th>
                  <th>Data de Nascimento</th>
                  <th>Status</th>
                  <th>Telefone</th>
                  <th>Email</th>
                  <th />
                </tr>
              </thead>

              <!-- loading state -->
              <template v-if="isLoadingUsers">
                <tbody>
                  <tr v-for="(_, index) in range((filters.page_size || 10) + 1)" :key="index">
                    <td v-for="i in range(8)" :key="i">
                      <div class="placeholder-glow" style="height: 19.5px;">
                        <span class="placeholder col-12" />
                      </div>
                    </td>
                  </tr>
                </tbody>
              </template>

              <!-- success state -->
              <tbody v-else>
                <tr v-for="(user, index) in users?.data" :key="index">
                  <!--  -->
                  <td>
                    <input
                      class="form-check-input m-0 align-middle table-selectable-check"
                      type="checkbox"
                      aria-label="Selecione este financiamento" />
                  </td>
                  <!-- id -->
                  <td>
                    <span class="text-secondary">{{ user.id }}</span>
                  </td>
                  <td>
                    <b class="text-reset" tabindex="-1">{{ user.fullname }}</b>
                  </td>
                  <td>
                    <b class="text-secondary">{{ user.partner?.document }}</b>
                  </td>
                  <td>
                    {{ user.birthdate ? date(user.birthdate).format('L') : '-' }}
                  </td>
                  <td>
                    <template v-if="user.active">
                      <span class="tag">
                        <span class="badge badge-3 text-indigo-fg tag-status badge-dot bg-success" />
                        Ativo
                      </span>
                    </template>
                    <template v-else>
                      <span class="tag">
                        <span class="badge badge-3 text-indigo-fg tag-status badge-dot bg-danger" />
                        Inativo
                      </span>
                    </template>
                  </td>
                  <td>
                    {{ user.contact.mobile }}
                  </td>
                  <td>
                    {{ user.contact.email }}
                  </td>
                  <td>
                    <RouterLink
                      :to="`users/${user.id}`"
                      class="btn btn-outline-primary"
                      title="Ver detalhes"
                    >
                      <Info class="icon icon-sm me-1" />
                      Detalhes
                    </RouterLink>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="card-footer d-flex align-items-center">
            <p class="m-0 text-secondary">
              Exibindo
              {{ (currentPage - 1) * pageSize + 1 }} ~
              {{ Math.min(currentPage * pageSize, totalItems) }}
              de <b>{{ totalItems }}</b> resultados
              • Página {{ currentPage }} de {{ totalPages }}
            </p>

            <ul class="pagination m-0 ms-auto">
              <div class="placeholder-glow">
                <span class="placeholder col-12" />
              </div>
            </ul>

            <ul class="pagination m-0 ms-auto">
              <template v-if="isLoadingUsers">
                <div class="placeholder-glow" style="width: 200px;">
                  <span class="placeholder col-12" />
                </div>
              </template>
              <template v-else>
                <!--  -->
                <li class="page-item">
                  <button class="page-link" @click.prevent="handlePreviousPage" type="button">
                    <ArrowLeft height="12" />Anterior
                  </button>
                </li>
                <!--  -->
                <li v-for="p in formPagination" class="page-item" :class="{ active: p.isActive }" :key="p.page">
                  <button @click="() => _handleSetPage(p.page)" class="page-link" type="button">{{ p.page }}</button>
                </li>

                <!--  -->
                <li class="page-item">
                  <button class="page-link" @click.prevent="handleNextPage" type="button">
                    Próxima <ArrowRight height="12" />
                  </button>
                </li>
              </template>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </DashboardLayout>
</template>

<style scoped lang="scss">
.table {
  tbody {
    tr {
      background-color: white !important;

      &:hover {
        background-color: #f8f9fa !important;
      }
    }
  }
}
</style>
